import React, { Fragment, useContext } from 'react';
import styled from 'styled-components';
import { Row, Col, Form } from 'react-bootstrap';
import { CustomButton, CustomSelect } from 'components/CustomForm';
import { FaCalendarAlt } from 'react-icons/fa';
import { Card, VDivider, EmptyLabel } from 'components/CommonWidgets';
import CommonTable from 'components/CommonTable';
import PersianPickerTile from 'components/PersianPickerTile';
import { getInputs } from 'network/apis/CommonApi';
import { useDispatch, useSelector } from 'react-redux';
import {
	buildFiltersQuery,
	handleDateRadioChange,
	handleInputChange,
	pickFromDate,
	pickToDate,
} from 'admin-app/redux/dashboard-filter.slice';
import { DefaultInputFilter, DefaultConcentrateFilter, DateRadio } from 'redux/global.consts';
import { useQuery } from 'react-query';
import Skeleton from 'react-loading-skeleton';
import { DashboardContext } from 'context/DashboardContext';
import useScreen from 'hooks/useScreen';
import { useAdminSelector, useAdminDispatch } from 'admin-app/redux/admin.store';

const Holder = styled.div`
	display: flex;
	flex-direction: column;
	align-items: end;
	padding-left: 1rem;
	padding-right: ${(props) => props.ismobile ? '1rem' : '3rem'};
	margin-top: ${(props) => props.ismobile ? '2rem' : '0'};
`;

const Check = styled(Form.Check)`
    display: inline;
`;

const ProductsSelect = styled(CustomSelect)`
    width: 50%;
`;

const Label = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const FormLabel = styled.label`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const StatisticsBox = styled.div`
	display: flex;
	flex-direction: column;
	flex-shrink: 2;
	width: 100%;
	align-items: end;
	border-left: ${(props) => props.ismobile ? 'none' : '1px solid #ababab'};
	margin-bottom: ${(props) => props.ismobile ? '1.5rem' : '0'};
	margin-left: 3rem;
`;

const getReportVerb = reportType => {
	if (reportType === 'distribute') {
		return 'توزیع';
	}

	return 'خرید';
};

const getReportMatter = reportType => {
	if (reportType === 'product') {
		return 'ترکیب';
	}

	return 'نهاده';
};

///reportType is an enum -> buy|distribute|product, default: buy
const TotalReportForm = ({ reportType = 'buy', data, isLoading = false }) => {
	const { currentTab } = useContext(DashboardContext);
	const { size, lessThan1200, lessThan900, lessThan500 } = useScreen();

	const filterState = useAdminSelector((store) => store.dashboardFilter);
	const dispatch = useAdminDispatch();

	const { data: inputsData } = useQuery(
		["get-inputs"],
		() => getInputs(),
		{
			refetchOnWindowFocus: false,
			enabled: currentTab === reportType,
		}
	);
	const getInputOtions = () => {
		if (inputsData?.data) {
			const inputOptions = inputsData.data.map((input, index) => {
				return { value: input?.item_id, label: input?.name };
			});

			return reportType === 'product'
				? [DefaultInputFilter, ...inputOptions]
				: [DefaultInputFilter, DefaultConcentrateFilter, ...inputOptions];
		}

		return [];
	};

	const getItems = () => data?.list?.data?.items ?? [];
	const getInputsCount = () => `${getItems().length}`.persianCurrency();
	const getInputLabel = (item) => {
		if (reportType === 'product') {
			return item?.['food_item']?.name ?? '';
		} else {
			return item?.input?.name || item?.input || '-';
		}

		return '-';
	};
	const getSum = () => `${getItems().reduce((accumulator, next) => accumulator + next.sum, 0)}`.persianCurrency();
	const getUnitName = () => data?.statistics?.data?.unit?.name ?? '';
	const isEmpty = () => data && !isLoading && data?.list?.data?.items?.length < 1;

	const getCalendarPickerTopOffset = () => {
		if (reportType === 'product') {
			return lessThan1200 ? '38%' : '19%';
		} else {
			return lessThan1200 ? '36%' : '18%';
		}
	};

	return (
		<Holder ismobile={lessThan900}>
			<div className="mb-2">گزارش کلی</div>
			<Card>
				<div className="d-flex justify-content-between align-items-center">
					<div className="d-inline-flex flex-wrap align-items-center">
						<PersianPickerTile
							currentDate={filterState.pickedDateTo}
							topOffset={getCalendarPickerTopOffset()}
							leftOffset={lessThan1200 ? '3%' : '37%'}
							onPick={(pickedDate) => dispatch(pickToDate(pickedDate))}
						/>
						<span className="mx-2">تا</span>
						<PersianPickerTile
							currentDate={filterState.pickedDateFrom}
							topOffset={getCalendarPickerTopOffset()}
							leftOffset={lessThan1200 ? '11%' : '49%'}
							onPick={(pickedDate) => dispatch(pickFromDate(pickedDate))}
						/>
					</div>
					<div className="d-inline-flex flex-wrap justify-content-end">
						<FormLabel htmlFor={DateRadio.CustomDate}>بازه زمانی دلخواه</FormLabel>
						<Check
							className={`${lessThan900 ? '' : 'me-3'} ms-2`}
							type="radio"
							id={DateRadio.CustomDate}
							checked={filterState.selectedRadio === DateRadio.CustomDate}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
						<FormLabel
							htmlFor={DateRadio.LastMonth}
							className={`${size.width < 560 ? 'mt-3' : ''}`}
						>
							{getReportVerb(reportType)} های یک ماه گذشته
						</FormLabel>
						<Check
							className={`ms-1 ${size.width < 560 ? 'mt-3' : ''}`}
							type="radio"
							id={DateRadio.LastMonth}
							checked={filterState.selectedRadio === DateRadio.LastMonth}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
					</div>
				</div>
				<div className="mt-3 d-flex justify-content-between align-items-center flex-wrap-reverse">
					<CustomButton
						className={`${lessThan900 ? 'w-100 mt-3' : ''}`}
						onClick={() => dispatch(buildFiltersQuery())}
					>
						نمایش
					</CustomButton>
					<div className='d-inline-flex align-items-center justify-content-end flex-grow-2'>
						<ProductsSelect
							className="w-100 mx-4"
							radius="18"
							options={getInputOtions()}
							onChange={(newInput) => dispatch(handleInputChange(newInput))}
							value={filterState.selectedInput}
							menuPosition='fixed'
						/>
						<FormLabel htmlFor={DateRadio.LastThreeMonths} className='text-end flex-shrink-0'>
							{getReportVerb(reportType)} های سه ماه گذشته
						</FormLabel>
						<Check
							className="ms-2"
							type="radio"
							id={DateRadio.LastThreeMonths}
							checked={filterState.selectedRadio === DateRadio.LastThreeMonths}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
					</div>
				</div>
			</Card>
			<Card className="mt-4">
				{isEmpty() ? (
					<EmptyLabel>
						رکوردی ثبت نشده است
					</EmptyLabel>
				) : (
					<div className={`
						d-flex 
						align-items-center 
						justify-content-between						
						${lessThan500 ? 'ps-0' : 'ps-4'}
						${lessThan500 ? 'flex-column-reverse' : ''}
					`}
					>
						<div className={`flex-shrink-3 ${lessThan500 ? 'w-100' : ''}`}>
							{isLoading ? (
								<Skeleton count={6} />
							) : (
								<CommonTable
									cols={['مقدار (تن)', `نوع ${getReportMatter(reportType)}`]}
									tablewidth="100%"
									maxHeight='14rem'
								>
									{getItems().map((item, index) => (
										<tr key={index}>
											<td>{`${item?.sum ?? 0}`.persianCurrency()}</td>
											<td>
												{getInputLabel(item)}
											</td>
										</tr>
									))}
								</CommonTable>
							)}
						</div>
						<StatisticsBox ismobile={lessThan500}>
							<span>آمار</span>
							{isLoading ? (
								<Skeleton count={3} width={200} height={40} />
							) : (
								<Fragment>
									<Row className="mt-2 me-2">
										<Col>
											<Label>{getInputsCount()}</Label>
										</Col>
										<Col>
											<Label>:انواع {getReportMatter(reportType)} ها</Label>
										</Col>
									</Row>
									<Row className="mt-2 me-2">
										<Col>
											<Label>{getSum()}</Label>
										</Col>
										<Col>
											<Label>:مجموع ({getUnitName()})</Label>
										</Col>
									</Row>
									{/* <Row className="mt-2 me-2">
										<Col>
											<Label>{getProvinceCount()}</Label>
										</Col>
										<Col>
											<Label>:تعداد استانها</Label>
										</Col>
									</Row> */}
								</Fragment>
							)}
						</StatisticsBox>
					</div>
				)}
			</Card>
		</Holder>
	);
};

export default TotalReportForm;
